import AMapLoader from "@amap/amap-jsapi-loader";
import { RightPanel, zoomLevelMax, zoomLevelMin } from "helpers/constants";
import { AppContext } from "helpers/context";
import { MapContext } from "helpers/map";
import { MapSize } from "helpers/models";
import { useContext, useEffect } from "react";
import styles from "./amap.module.scss";
import DistanceMeter from "./distance-meter/distance-meter";
import LayerSwitch from "./layer-switch/layer-switch";
import Marker from "./map-marker/map-marker";
import MultipleMarker from "./map-marker/map-multiple-marker";
import EventsMarker from "./map-marker/events-multiple-marker";
import WorldZoom from "./world-zoom/world-zoom";
import Zooms from "./zooms/zooms";
import { getStyleIndex, styleArr, getMarkerUrl, handleFilterLocations } from "helpers/helpers";

const amapkey = "aee6edc014f6b2e1f310095a5c40a949";

interface Props {
	size: string;
}

function AMap(props: Props) {
	const [context] = useContext(AppContext);
	const { setAMap, map, setMap, } = useContext(MapContext);
	const unit = context.currentMultipleLocations?.aggregationUnit ?? "numberOfLocations";
	const isNumberOfLocations = unit === "numberOfLocations";

	const mapClass = () => {
		switch (props.size) {
			case MapSize.XS:
				return styles.mapXS;
			case MapSize.S:
				return styles.mapS;
			case MapSize.M:
				return styles.mapM;
			case MapSize.L:
			default:
				return styles.mapL;
		}
	};

	const handleLocationsMarkerDom = () => {
		const current = context.currentMultipleLocations;
		if (!current) return null;

		// 先渲染 checked 项
		const checkedItems: any[] = handleFilterLocations({
			data: current.items || [],
			unit,
			multipleLocationsSearch: context.multipleLocationsSearch,
			locationsFilterInfo: context.locationsFilterInfo,
			filterChecked: true
		});
		if (checkedItems.length === 0) return null;

		// 当 unit 为 numberOfLocations 时进行分级处理
		if (isNumberOfLocations) {
			const imageUrl = checkedItems.length > 9 ? getMarkerUrl() : getMarkerUrl("#001789");

			// 大量点位按聚合渲染，少量点位按 icon 渲染
			return checkedItems.length > 50 ? (
				<MultipleMarker locations={checkedItems} />
			) : (
				checkedItems.map((location: any) => (
					<Marker
						key={location.id}
						position={[location.longitude, location.latitude]}
						locationData={location}
						imageUrl={imageUrl}
					/>
				))
			);
		}

		// 其余 unit 按 total/min 做二次分段
		let total = 0;
		let min = Infinity;
		current.items?.forEach((it: any) => {
			const v = it?.[unit];
			// 先做数值规整：v && Math.round(v)
			if (v && Math.round(v)) {
				total += v;
				if (v < min) min = v;
			}
		});

		const canColorByBucket = min !== Infinity && total - min > 9;

		if (checkedItems.length > 50) {
			// 数量大于 50 仍使用 MultipleMarker，保证展示稳定
			return <MultipleMarker locations={checkedItems} total={total} min={min} />;
		}

		// 单点渲染时根据 canColorByBucket 控制颜色分段
		return checkedItems.map((location: any) => {
			let imageUrl: string;

			if (canColorByBucket) {
				const idx = getStyleIndex(location[unit], total, min);
				imageUrl = getMarkerUrl(styleArr[idx].bg)
			} else {
				imageUrl = getMarkerUrl("#001789")
			}

			return (
				<Marker
					key={location.id}
					position={[location.longitude, location.latitude]}
					locationData={location}
					imageUrl={imageUrl}
				/>
			);
		});
	};

	/** 初始化高德地图并保证多语言可用 */
	useEffect(() => {
		(window as any)._AMapSecurityConfig = {
			securityJsCode: "1ff8751ba0f4086ec444b1acbb56f14a",
		};

		AMapLoader.load({
			key: amapkey,
			version: "2.0",
			plugins: [
				"AMap.Scale",
				"AMap.HawkEye",
				"AMap.ToolBar",
				"AMap.ControlBar",
				"AMap.MapType",
				"AMap.MouseTool",
				"AMap.MarkerCluster",
				"AMap.PolylineEditor"
			],
		})
			.then((AMap) => {
				setAMap(AMap);

				const scale = new AMap.Scale({
					position: { bottom: "10px", right: "160px" },
				});
				const overView = new AMap.HawkEye({ opened: false });

				const mapInstance = new AMap.Map("container", {
					viewMode: "3D",
					zoom: context.mapZoom,
					center: context.mapCenter,
					resizeEnable: true,
					vectorMapForeign: "style_en",
					WebGLParams: { preserveDrawingBuffer: true },
					zooms: [zoomLevelMin, zoomLevelMax],
					mapStyle: "amap://styles/normal",
				});
				mapInstance.addControl(scale);
				mapInstance.addControl(overView);

				setMap(mapInstance);
			})
			.catch((e) => {
				console.log(e);
			});

		return () => {
			map?.destroy();
		};
	}, []); // 空依赖，仅初始化一次

	return (
		<div className="flex items-center">
			<div className={mapClass()} style={{ position: "relative" }}>
				<div id="container" style={{ height: "100%", minWidth: "100px" }} />
				{/* <MapInfo></MapInfo> */}
				<DistanceMeter />
				{/* 避免重复挂载导致图层异常 */}
				{/* <ScreenShot></ScreenShot> */}
				<LayerSwitch></LayerSwitch>
				<WorldZoom></WorldZoom>
				<Zooms></Zooms>

				{context.searchLocationInfo?.[context.language] &&
					context.searchMapCenter &&
					context.rightPanel !== RightPanel.Empty && (
						<Marker position={context.searchMapCenter} isLabel={true} isLocation={true} />
					)}

				{handleLocationsMarkerDom()}

				{context.eventsList && context.eventsList.length > 0 && <EventsMarker />}
			</div>
		</div>
	);
}

export default AMap;

